// Rail arrows and scroll progress for sections/swist-showcase.liquid (loaded once per page, deferred).
if (!customElements.get('swist-showcase')) {
  customElements.define(
    'swist-showcase',
    /** Rail arrows and scroll progress; card behavior lives in swist-card.js. */
    class SwistShowcase extends HTMLElement {
      #abort = new AbortController();
      #frame = 0;

      connectedCallback() {
        // Later instances upgrade as soon as their start tag is parsed, before their list exists
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.#setup(), {
            once: true,
            signal: this.#abort.signal,
          });
          return;
        }
        this.#setup();
      }

      #setup() {
        const { signal } = this.#abort;
        const root = this.closest('.ssc');
        this.rail = this.querySelector('[data-ssc-rail]');
        this.prev = root?.querySelector('[data-ssc-prev]') ?? null;
        this.next = root?.querySelector('[data-ssc-next]') ?? null;
        this.isRail = root?.classList.contains('ssc--rail') ?? false;

        this.#loadSource(signal);

        if (!this.isRail || !this.rail) return;
        this.#watchDeferred(signal);
        this.prev?.addEventListener('click', () => this.#step(-1), { signal });
        this.next?.addEventListener('click', () => this.#step(1), { signal });
        this.rail.addEventListener('scroll', () => this.#queueUpdate(), { passive: true, signal });
        window.addEventListener('resize', () => this.#queueUpdate(), { passive: true, signal });
        this.#update();
      }

      disconnectedCallback() {
        this.#abort.abort();
        this.#abort = new AbortController();
        cancelAnimationFrame(this.#frame);
      }

      /** Fills a related/recent rail after the page loads; hides the section when it stays empty. */
      async #loadSource(signal) {
        if (this.hasAttribute('data-empty')) {
          this.#hideSection();
          return;
        }
        let url = this.dataset.fetchUrl ?? '';

        if (this.hasAttribute('data-recent')) {
          const key = 'swist:recently-viewed';
          const current = this.dataset.currentProduct ?? '';
          let ids = [];
          try {
            ids = JSON.parse(localStorage.getItem(key) ?? '[]');
          } catch (error) {
            ids = [];
          }
          ids = (Array.isArray(ids) ? ids : []).map(String).filter((id) => id && id !== current);
          if (current) {
            try {
              localStorage.setItem(key, JSON.stringify([current, ...ids].slice(0, 20)));
            } catch (error) {
              /* storage unavailable: the rail simply stays empty */
            }
          }

          const wanted = ids.slice(0, Number(this.dataset.recentLimit) || 8);
          if (wanted.length === 0) {
            this.#hideSection();
            return;
          }
          const query = new URLSearchParams({
            q: wanted.map((id) => `id:${id}`).join(' OR '),
            type: 'product',
            'options[unavailable_products]': 'last',
            section_id: 'swist-showcase',
          });
          url = `${this.dataset.searchUrl ?? '/search'}?${query}`;
        }

        if (!url) return;

        // Below-the-fold rails fetch once they're near the viewport, not during the page load
        await this.#whenNear(signal);
        if (signal.aborted) return;

        this.setAttribute('data-loading', '');
        try {
          const response = await fetch(url, { signal });
          if (!response.ok) throw new Error(String(response.status));
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const items = doc.querySelectorAll(
            '[data-ssc-rail] > .ssc__item:not(.ssc__item--skeleton):not(.ssc__item--placeholder)'
          );
          if (items.length === 0 || !this.rail) {
            this.#hideSection();
            return;
          }
          this.rail.replaceChildren(...items);
          this.classList.add('is-loaded');
          if (this.isRail) {
            this.#update();
            this.#watchDeferred(signal);
          }
        } catch (error) {
          if (!signal.aborted) this.#hideSection();
        } finally {
          this.removeAttribute('data-loading');
        }
      }

      /**
       * Resolves once the rail is within ~800px of the viewport (or the element is torn down).
       * @param {AbortSignal} signal
       * @returns {Promise<void>}
       */
      #whenNear(signal) {
        return new Promise((resolve) => {
          const observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              observer.disconnect();
              resolve();
            },
            { rootMargin: '800px 0px' }
          );
          observer.observe(this);
          signal.addEventListener(
            'abort',
            () => {
              observer.disconnect();
              resolve();
            },
            { once: true }
          );
        });
      }

      /**
       * Rail cards past the first two render their photos as data-swc-src (snippets/swist-card): the browser
       * would otherwise download every card near the viewport, including the ones off-screen to the side.
       * Once the rail nears the viewport, cards load as they come into the rail's view (about a card ahead);
       * touching, focusing or clicking the rail loads the rest at once so swiping never shows a blank card.
       * @param {AbortSignal} signal
       */
      #watchDeferred(signal) {
        const rail = this.rail;
        if (!rail?.querySelector('img[data-swc-defer]')) return;

        /** @param {Element} img */
        const show = (img) => {
          if (!(img instanceof HTMLImageElement) || !img.hasAttribute('data-swc-src')) return;
          const srcset = img.getAttribute('data-swc-srcset');
          if (srcset) img.srcset = srcset;
          img.src = img.getAttribute('data-swc-src') ?? '';
          img.removeAttribute('data-swc-src');
          img.removeAttribute('data-swc-srcset');
          img.removeAttribute('data-swc-defer');
        };
        const showAll = () => rail.querySelectorAll('img[data-swc-defer]').forEach(show);

        const inRail = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              show(entry.target);
              inRail.unobserve(entry.target);
            }
          },
          { root: rail, rootMargin: '0px 50% 0px 0px' }
        );
        const near = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            near.disconnect();
            rail.querySelectorAll('img[data-swc-defer]').forEach((img) => inRail.observe(img));
          },
          { rootMargin: '600px 0px' }
        );
        near.observe(this);

        // The arrows live next to this element, inside the section's .ssc wrapper
        const area = this.closest('.ssc') ?? this;
        for (const type of ['touchstart', 'focusin', 'click']) {
          area.addEventListener(type, showAll, { once: true, passive: true, signal });
        }
        signal.addEventListener(
          'abort',
          () => {
            near.disconnect();
            inRail.disconnect();
          },
          { once: true }
        );
      }

      #hideSection() {
        const section = this.closest('.shopify-section') ?? this.closest('.ssc');
        if (section instanceof HTMLElement) section.hidden = true;
      }

      #step(direction) {
        const item = this.rail.querySelector('.ssc__item');
        if (!(item instanceof HTMLElement)) return;
        const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
        const perView = Math.max(1, Math.floor((this.rail.clientWidth + gap) / (item.offsetWidth + gap)));
        const isRtl = getComputedStyle(this.rail).direction === 'rtl';
        this.rail.scrollBy({
          left: direction * (isRtl ? -1 : 1) * perView * (item.offsetWidth + gap),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      #queueUpdate() {
        cancelAnimationFrame(this.#frame);
        this.#frame = requestAnimationFrame(() => this.#update());
      }

      #update() {
        const max = this.rail.scrollWidth - this.rail.clientWidth;
        const pos = Math.abs(this.rail.scrollLeft);
        const bar = this.querySelector('[data-ssc-scrollbar]');
        if (bar instanceof HTMLElement) {
          const size = max > 0 ? this.rail.clientWidth / this.rail.scrollWidth : 1;
          const ratio = max > 0 ? pos / max : 0;
          bar.style.width = `${size * 100}%`;
          bar.style.transform = `translateX(${((ratio * (1 - size)) / size) * 100}%)`;
        }
        if (this.prev) this.prev.disabled = pos <= 2;
        if (this.next) this.next.disabled = pos >= max - 2;
      }
    }
  );
}
